import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { type GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams'
import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'
import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { type OrganisationDto, useGetTerminalOrganisations } from '@dhl/mdp-api'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField, useSearchAndPage } from '~/common'

export const TerminalsPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize, search } = useSearchAndPage()

  const { data, isFetching } = useGetTerminalOrganisations(searchAndPageParams)

  const handleRowClick = (row: OrganisationDto) => {
    navigate({ pathname: links.terminalDetailsLink(row.id), search: `search=${search}` })
  }

  const renderCellName = (value: never, row: OrganisationDto) => {
    return row.name ? row.name : ''
  }

  const renderCellAddress = (value: never, row: OrganisationDto) => {
    return row.data.address?.street && row.data.address.number
      ? `${row.data.address.street} 
         ${row.data.address.number},
         ${row.data.address.zipCode},
         ${row.data.address.city}`
      : ''
  }

  const renderCellContact = (value: never, row: OrganisationDto) => {
    return row.data.contactPerson?.firstName && row.data.contactPerson.lastName
      ? `${row.data.contactPerson.firstName} ${row.data.contactPerson.lastName}`
      : ''
  }

  const renderCellEmail = (value: never, row: OrganisationDto) => {
    return row.data.contactPerson?.email.address ? row.data.contactPerson.email.address : ''
  }

  const renderCellPhone = (value: never, row: OrganisationDto) => {
    return row.data.contactPerson?.phone ? row.data.contactPerson.phone : ''
  }

  const renderCellEdit = (param: GridRenderCellParams) => {
    return (
      <IconButton aria-label="edit terminal" onClick={() => navigate(links.editTerminalLink(param.row.id))}>
        <EditSolidIcon />
      </IconButton>
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('admin.terminalsPage.name'),
      flex: 1,
      valueGetter: renderCellName,
    },
    {
      field: 'address',
      headerName: t('admin.terminalsPage.address'),
      flex: 1,
      valueGetter: renderCellAddress,
    },
    {
      field: 'contact',
      headerName: t('admin.terminalsPage.contact'),
      flex: 1,
      valueGetter: renderCellContact,
    },
    {
      field: 'email',
      headerName: t('admin.terminalsPage.emailAddress'),
      flex: 1,
      valueGetter: renderCellEmail,
    },
    {
      field: 'phone',
      headerName: t('admin.terminalsPage.phone'),
      flex: 1,
      type: 'number',
      valueGetter: renderCellPhone,
    },
    {
      field: 'actions',
      headerName: t('admin.terminalsPage.actions'),
      flex: 1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: renderCellEdit,
    },
  ]

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" data-testid="terminals-page">
        <Grid sx={{ backgroundColor: 'common.white' }}>
          <SearchTextField onChange={handleSearch} transform={lowercaseTransform} />
        </Grid>
        <Grid>
          <Button
            data-testid="add-terminal"
            color="primary"
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigate(links.addTerminalLink())}
          >
            {t('admin.terminalsPage.addNewTerminal')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={isFetching}
            data-testid="terminals-table"
            autoHeight
            rows={data?.content ?? []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={data?.totalCount ?? 0}
            onPageChange={(_, page) => {
              setPage(page)
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(pageSize) => {
              setPageSize(pageSize)
            }}
          />
        </Box>
      </Box>
    </>
  )
}
